import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SubscriptionStatus, UpgradeInfo, FeatureAccessItem, UsageSummaryItem } from '../types';
import { getSubscriptionStatus } from '../services/subscription.service';
import UpgradeModal from '../components/Subscription/UpgradeModal';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  subscription: SubscriptionStatus['subscription'];
  trial: SubscriptionStatus['trial'];
  permissions: SubscriptionStatus['permissions'];
  featureAccess: Record<string, FeatureAccessItem>;
  usageSummary: UsageSummaryItem[];
  display: SubscriptionStatus['display'];
  loading: boolean;
  isAdmin: boolean;
  refreshSubscription: () => Promise<void>;
  upgradeInfo: UpgradeInfo | null;
  showUpgradePrompt: (info: UpgradeInfo) => void;
  dismissUpgradePrompt: () => void;
  canAccess: (featureKey: string) => boolean;
  getUsage: (featureKey: string) => number;
  getLimit: (featureKey: string) => number | null;
  isFeatureLimited: (featureKey: string) => boolean;
  getFeatureAccess: (featureKey: string) => FeatureAccessItem | null;
  getUsageSummaryItem: (featureKey: string) => UsageSummaryItem | null;
}

const defaultPermissions = {
  quickSearch: false,
  deepSearch: false,
  contactEnrichment: false,
};

const openPermissions = {
  quickSearch: true,
  deepSearch: true,
  contactEnrichment: true,
};

const defaultDisplay = {
  statusLabel: '',
  tierLabel: '',
};

const SubscriptionContext = createContext<SubscriptionContextType>({
  subscription: null,
  trial: null,
  permissions: defaultPermissions,
  featureAccess: {},
  usageSummary: [],
  display: defaultDisplay,
  loading: true,
  isAdmin: false,
  refreshSubscription: async () => {},
  upgradeInfo: null,
  showUpgradePrompt: () => {},
  dismissUpgradePrompt: () => {},
  canAccess: () => true,
  getUsage: () => 0,
  getLimit: () => null,
  isFeatureLimited: () => false,
  getFeatureAccess: () => null,
  getUsageSummaryItem: () => null,
});

export const useSubscription = () => useContext(SubscriptionContext);

const SUBSCRIPTIONS_ENABLED = import.meta.env.VITE_ENABLE_SUBSCRIPTIONS === 'true';

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus['subscription']>(null);
  const [trial, setTrial] = useState<SubscriptionStatus['trial']>(null);
  const [permissions, setPermissions] = useState<SubscriptionStatus['permissions']>(SUBSCRIPTIONS_ENABLED ? defaultPermissions : openPermissions);
  const [featureAccess, setFeatureAccess] = useState<Record<string, FeatureAccessItem>>({});
  const [usageSummary, setUsageSummary] = useState<UsageSummaryItem[]>([]);
  const [display, setDisplay] = useState<SubscriptionStatus['display']>(defaultDisplay);
  const [loading, setLoading] = useState(SUBSCRIPTIONS_ENABLED);
  const [isAdmin, setIsAdmin] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState<UpgradeInfo | null>(null);
  const suppressUntilRef = useRef<number>(0);
  const location = useLocation();
  const { user } = useAuth();

  const fetchStatus = useCallback(async () => {
    if (!SUBSCRIPTIONS_ENABLED) return;

    setLoading(true);

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const data = await getSubscriptionStatus();
      setIsAdmin(data.isAdmin === true);
      setSubscription(data.subscription);
      setTrial(data.trial);
      setPermissions(data.permissions);
      setFeatureAccess(data.featureAccess || {});
      setUsageSummary(data.usageSummary || []);
      setDisplay(data.display);
    } catch {
      setIsAdmin(false);
      setSubscription(null);
      setTrial(null);
      setPermissions(defaultPermissions);
      setFeatureAccess({});
      setUsageSummary([]);
      setDisplay(defaultDisplay);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!SUBSCRIPTIONS_ENABLED) {
      setLoading(false);
      return;
    }
    if (user) {
      fetchStatus();
    } else {
      setIsAdmin(false);
      setSubscription(null);
      setTrial(null);
      setPermissions(defaultPermissions);
      setFeatureAccess({});
      setUsageSummary([]);
      setDisplay(defaultDisplay);
      setLoading(false);
    }
  }, [user, fetchStatus]);

  useEffect(() => {
    if (!SUBSCRIPTIONS_ENABLED) return;
    const params = new URLSearchParams(location.search);
    if (params.get('checkout') === 'success') {
      fetchStatus();
    }
  }, [location.search, fetchStatus]);

  useEffect(() => {
    if (!SUBSCRIPTIONS_ENABLED) return;
    const handler = (e: Event) => {
      if (Date.now() < suppressUntilRef.current) return;
      if (location.pathname.startsWith('/pricing')) return;
      const customEvent = e as CustomEvent;
      setUpgradeInfo(customEvent.detail);
    };
    window.addEventListener('subscription-upgrade-required', handler);
    return () => window.removeEventListener('subscription-upgrade-required', handler);
  }, [location.pathname]);

  useEffect(() => {
    if (!SUBSCRIPTIONS_ENABLED) return;
    const handler = () => {
      fetchStatus();
    };
    window.addEventListener('subscription-usage-changed', handler);
    return () => window.removeEventListener('subscription-usage-changed', handler);
  }, [fetchStatus]);

  const showUpgradePrompt = useCallback((info: UpgradeInfo) => {
    if (location.pathname.startsWith('/pricing')) return;
    setUpgradeInfo(info);
  }, [location.pathname]);

  const dismissUpgradePrompt = useCallback(() => {
    suppressUntilRef.current = Date.now() + 2000;
    setUpgradeInfo(null);
  }, []);

  const canAccess = useCallback((featureKey: string): boolean => {
    if (!SUBSCRIPTIONS_ENABLED) return true;
    if (isAdmin) return true;
    const access = featureAccess[featureKey];
    if (!access) return true;
    return access.enabled;
  }, [isAdmin, featureAccess]);

  const getUsage = useCallback((featureKey: string): number => {
    if (!SUBSCRIPTIONS_ENABLED) return 0;
    if (isAdmin) return 0;
    const item = usageSummary.find(u => u.featureKey === featureKey);
    return item?.currentUsage ?? 0;
  }, [isAdmin, usageSummary]);

  const getLimit = useCallback((featureKey: string): number | null => {
    if (!SUBSCRIPTIONS_ENABLED) return null;
    if (isAdmin) return null;
    const item = usageSummary.find(u => u.featureKey === featureKey);
    return item?.limit ?? null;
  }, [isAdmin, usageSummary]);

  const isFeatureLimited = useCallback((featureKey: string): boolean => {
    if (!SUBSCRIPTIONS_ENABLED) return false;
    if (isAdmin) return false;
    const item = usageSummary.find(u => u.featureKey === featureKey);
    if (!item || item.limit === null) return false;
    return item.currentUsage >= item.limit;
  }, [isAdmin, usageSummary]);

  const getFeatureAccess = useCallback((featureKey: string): FeatureAccessItem | null => {
    if (!SUBSCRIPTIONS_ENABLED) return null;
    return featureAccess[featureKey] || null;
  }, [featureAccess]);

  const getUsageSummaryItem = useCallback((featureKey: string): UsageSummaryItem | null => {
    if (!SUBSCRIPTIONS_ENABLED) return null;
    if (isAdmin) return null;
    return usageSummary.find(u => u.featureKey === featureKey) || null;
  }, [isAdmin, usageSummary]);

  const value = {
    subscription,
    trial,
    permissions,
    featureAccess,
    usageSummary,
    display,
    loading,
    isAdmin,
    refreshSubscription: fetchStatus,
    upgradeInfo,
    showUpgradePrompt,
    dismissUpgradePrompt,
    canAccess,
    getUsage,
    getLimit,
    isFeatureLimited,
    getFeatureAccess,
    getUsageSummaryItem,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      {SUBSCRIPTIONS_ENABLED && !isAdmin && upgradeInfo && (
        <UpgradeModal
          upgradeInfo={upgradeInfo}
          onClose={dismissUpgradePrompt}
        />
      )}
    </SubscriptionContext.Provider>
  );
};
